import {
  Center,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { Condition } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useTranslation } from 'react-i18next';
import { ResourceSearchForm } from '@/components/resource-search/ResourceSearchForm';
import { ResourceTable } from '@/components/resource-search/ResourceTable';
import {
  getConditionSearchFields,
  getConditionTableColumns,
  CONDITION_SEARCH_DEFAULTS,
  type ConditionSearchParams,
} from './diagnosisSearchConfig';
import { useConditionSearch } from './useConditionSearch';
import { useCallback, useMemo, useState } from 'react';
import { mapConditionsToInputItems } from '@/utils/condition.utils';
import { ConditionListItem } from '../examination/conditions/ConditionListItem';

interface DiagnosesPageProps {
  patientId?: string;
}

export function DiagnosesPage({ patientId }: DiagnosesPageProps): JSX.Element {
  const medplum = useMedplum();
  const { t } = useTranslation(['patient-pages', 'lims']);
  const [selectedCondition, setSelectedCondition] = useState<Condition | null>(
    null
  );

  const {
    resources: conditions,
    loading,
    page,
    totalPages,
    setPage,
    patients,
    searchParams,
    updateSearchParams,
    clearSearch,
    fetchResources,
    patientNameQuery,
    setPatientNameQuery,
    setPatientQuery,
    patientNameOptions,
    patientLoading,
    patientNameLoading,
  } = useConditionSearch(medplum, patientId);

  const hidePatientName = Boolean(patientId);

  const handleSearch = useCallback(
    (updates: Partial<ConditionSearchParams>) => {
      const mergedParams = { ...searchParams, ...updates };
      setPage(1);
      updateSearchParams(updates);
      fetchResources(mergedParams);
    },
    [fetchResources, searchParams, setPage, updateSearchParams]
  );

  const handleClear = useCallback(() => {
    clearSearch(CONDITION_SEARCH_DEFAULTS);
  }, [clearSearch]);

  const fields = useMemo(
    () =>
      getConditionSearchFields({
        t,
        searchParams,
        onSearch: handleSearch,
        hidePatientName,
        patientNameQuery,
        setPatientNameQuery,
        setPatientQuery,
        patientNameOptions,
        patientLoading,
        patientNameLoading,
      }),
    [
      handleSearch,
      hidePatientName,
      patientLoading,
      patientNameLoading,
      patientNameOptions,
      patientNameQuery,
      searchParams,
      setPatientNameQuery,
      setPatientQuery,
      t,
    ]
  );

  const columns = useMemo(
    () =>
      getConditionTableColumns({
        t,
        patients,
        hidePatientColumn: hidePatientName,
      }),
    [hidePatientName, patients, t]
  );

  const handleConditionClick = useCallback((condition: Condition) => {
    setSelectedCondition(condition);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setSelectedCondition(null);
  }, []);

  return (
    <Stack gap="md" className="p-6">
      {!patientId && (
        <Title order={2}>
          {t('diagnosis.title', { defaultValue: 'Diagnoses' })}
        </Title>
      )}

      <ResourceSearchForm<ConditionSearchParams>
        fields={fields}
        searchParams={searchParams}
        onSearch={handleSearch}
        onClear={handleClear}
        clearLabel={t('diagnosis.filters.clear', { defaultValue: 'Clear' })}
        layout={{ cols: { base: 1, sm: 2, lg: 3 } }}
      />

      <Paper shadow="sm" withBorder>
        {loading ? (
          <Center p="xl">
            <Loader />
          </Center>
        ) : conditions.length === 0 ? (
          <Center p="xl">
            <Text c="dimmed">
              {t('diagnosis.table.empty', {
                defaultValue: 'No diagnoses found',
              })}
            </Text>
          </Center>
        ) : (
          <ResourceTable<Condition>
            data={conditions}
            columns={columns}
            page={page}
            totalPages={totalPages}
            onPageChange={setPage}
            onRowClick={handleConditionClick}
            onActionClick={handleConditionClick}
            emptyText={t('diagnosis.table.empty', {
              defaultValue: 'No diagnoses found',
            })}
          />
        )}
      </Paper>
      {/* Condition Detail Modal */}
      <Modal
        opened={!!selectedCondition}
        onClose={handleCloseDrawer}
        size="lg"
        title={t('diagnosis.viewModal.title', {
          defaultValue: 'View Diagnosis',
        })}
      >
        {selectedCondition &&
          (() => {
            const [conditionInput] = mapConditionsToInputItems([
              selectedCondition,
            ]);
            return conditionInput ? (
              <ConditionListItem
                condition={conditionInput}
                onAction={() => {}}
                isDeleting={false}
                hideActions={true}
              />
            ) : null;
          })()}
      </Modal>
    </Stack>
  );
}
